import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import MobileReaderChrome from './MobileReaderChrome';
import MobileAuthoredPage from './MobileAuthoredPage';
import {
  AUTHORED_EDITOR_EXTENSION_SET_VERSION,
  getAuthoredEditorExtensions,
} from '../../authored/tiptapExtensions';
import { resolveBookTextDir } from '../../authored/rtlEditorAlign';
import {
  PITCH_BLACK_THEME_ID,
  READER_FONT_SCALE_MAX,
  READER_FONT_SCALE_MIN,
  READER_FONT_STORAGE_KEY,
} from '../../reader/readerConstants';
import useReaderAffiliatePromo from '../../hooks/useReaderAffiliatePromo';

const FALLBACK_BG_THEME = {
  theme_meta_unique_id: 'fallback-bg',
  theme_name: 'Default',
  theme_url: '/images/wood.jpg',
};

const FALLBACK_PAGE_THEME = {
  theme_meta_unique_id: 'fallback-page',
  theme_name: 'Default',
  theme_url: '/images/clear-white-plaster-texture-pattern.jpg',
  is_available: 1,
};

export default function MobileAuthoredReader({ bookId, previewMode = false }) {
  const { request } = useApi();
  const navigate = useNavigate();
  const userNavigatedRef = useRef(false);
  const { recordForwardFlip, promoElement } = useReaderAffiliatePromo('mobile_authored_reader', {
    flipsPerPromo: 8,
  });

  const [book, setBook] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [backgroundThemes, setBackgroundThemes] = useState([]);
  const [selectedBackgroundTheme, setSelectedBackgroundTheme] = useState(FALLBACK_BG_THEME);
  const [selectedPageTheme, setSelectedPageTheme] = useState(FALLBACK_PAGE_THEME);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const [readerFontScale, setReaderFontScale] = useState(() => {
    try {
      const raw = localStorage.getItem(READER_FONT_STORAGE_KEY);
      if (raw == null || raw === '') return 1;
      const v = parseFloat(raw);
      if (Number.isNaN(v)) return 1;
      return Math.min(READER_FONT_SCALE_MAX, Math.max(READER_FONT_SCALE_MIN, v));
    } catch {
      return 1;
    }
  });

  const clampedFontScale = useMemo(
    () => Math.min(READER_FONT_SCALE_MAX, Math.max(READER_FONT_SCALE_MIN, readerFontScale)),
    [readerFontScale],
  );

  const readerTextDir = useMemo(() => resolveBookTextDir(book), [book]);

  const extensions = useMemo(
    () => getAuthoredEditorExtensions({ editable: false }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [AUTHORED_EDITOR_EXTENSION_SET_VERSION],
  );

  const updateUserBookDetail = useCallback(
    async ({ theme_id, last_read_page } = {}) => {
      if (previewMode) return;
      if (!localStorage.getItem('access_token')) return;
      const payload = {};
      if (theme_id !== undefined) payload.theme_id = theme_id;
      if (last_read_page !== undefined) payload.last_read_page = last_read_page;
      if (Object.keys(payload).length === 0) return;
      try {
        await request({
          method: 'patch',
          url: `/api/v1/pdf_parser/my_book_view/${bookId}/`,
          withAuth: true,
          data: payload,
        });
      } catch (err) {
        console.error('Mobile authored reader progress save error:', err);
      }
    },
    [bookId, previewMode, request],
  );

  useEffect(() => {
    let active = true;

    (async () => {
      setInitialLoading(true);
      setError(null);
      userNavigatedRef.current = false;

      const token = localStorage.getItem('access_token');
      let userId = 'guest';
      if (token) userId = localStorage.getItem('user_id') || 'guest';

      try {
        let b;
        let mode = 'public';
        if (token) {
          try {
            const authorRes = await request({
              method: 'get',
              url: `/api/v1/pdf_parser/author/books/${bookId}/`,
              withAuth: true,
            });
            b = authorRes.book;
            mode = 'author';
          } catch {
            const pubRes = await request({
              method: 'get',
              url: `/api/v1/pdf_parser/authored-books/${bookId}/`,
              withAuth: false,
            });
            b = pubRes.book;
          }
        } else {
          const pubRes = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/authored-books/${bookId}/`,
            withAuth: false,
          });
          b = pubRes.book;
        }

        if (!active || !b) {
          if (active) setError('not_found');
          return;
        }
        setBook(b);

        const pagesUrl =
          mode === 'author'
            ? `/api/v1/pdf_parser/author/books/${bookId}/pages/`
            : `/api/v1/pdf_parser/authored-books/${bookId}/pages/`;

        const pdata = await request({
          method: 'get',
          url: pagesUrl,
          params: { start: 0, end: 199 },
          withAuth: mode === 'author',
        });
        const pageList = pdata.pages || [];
        if (!active) return;
        setPages(pageList);

        let bt = [];
        try {
          const themeData = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/themes/${userId}/`,
          });
          bt = (themeData?.book_themes || []).filter(
            (t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID,
          );
          setBackgroundThemes(bt);
          setSelectedBackgroundTheme(bt[0] || FALLBACK_BG_THEME);
          setSelectedPageTheme(themeData?.page_themes?.[0] || FALLBACK_PAGE_THEME);
        } catch {
          setBackgroundThemes([]);
          setSelectedBackgroundTheme(FALLBACK_BG_THEME);
          setSelectedPageTheme(FALLBACK_PAGE_THEME);
        }

        if (token && !previewMode) {
          try {
            const userData = await request({
              method: 'get',
              url: `/api/v1/pdf_parser/my_book_view/${bookId}/`,
              withAuth: true,
            });
            const bd = userData?.book_detail;
            if (bd && active && !userNavigatedRef.current) {
              const rawLp = bd.last_read_page;
              const spreadIndex =
                rawLp != null && rawLp !== '' ? parseInt(String(rawLp), 10) : 0;
              const safeSpread = Number.isNaN(spreadIndex) ? 0 : Math.max(0, spreadIndex);
              const resumePageIndex = Math.min(
                Math.max(0, pageList.length - 1),
                safeSpread * 2,
              );
              setPageIndex(resumePageIndex);

              const tid =
                typeof bd.theme_id === 'object' && bd.theme_id !== null
                  ? bd.theme_id.theme_meta_unique_id || bd.theme_id.theme_id
                  : bd.theme_id;
              if (tid && bt.length) {
                const matched = bt.find((t) => t.theme_meta_unique_id === tid);
                if (matched) setSelectedBackgroundTheme(matched);
              }
            }
          } catch {
            /* start at page 0 */
          }
        }
      } catch (err) {
        console.error('Mobile authored reader init error:', err);
        if (active) setError('load_failed');
      } finally {
        if (active) setInitialLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [bookId, previewMode, request]);

  useEffect(() => {
    localStorage.setItem(READER_FONT_STORAGE_KEY, String(clampedFontScale));
  }, [clampedFontScale]);

  useEffect(() => {
    if (initialLoading || pages.length === 0 || !userNavigatedRef.current) return;
    const spreadIndex = Math.floor(pageIndex / 2);
    void updateUserBookDetail({ last_read_page: spreadIndex });
  }, [pageIndex, initialLoading, pages.length, updateUserBookDetail]);

  const backgroundStyle = useMemo(() => {
    const url = selectedBackgroundTheme?.theme_url;
    if (!url) return { backgroundColor: '#f8fafc' };
    return {
      backgroundImage: `url(${url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    };
  }, [selectedBackgroundTheme]);

  const currentPage = pages[pageIndex] || null;
  const canPrev = pageIndex > 0;
  const canNext = pages.length > 0 && pageIndex < pages.length - 1;

  const goPrev = () => {
    if (!canPrev) return;
    userNavigatedRef.current = true;
    setPageIndex((i) => i - 1);
  };

  const goNext = () => {
    if (!canNext) return;
    userNavigatedRef.current = true;
    setPageIndex((i) => i + 1);
    recordForwardFlip();
  };

  const jumpToPage = useCallback(
    (physicalPage1Based) => {
      if (!pages.length) return;
      const nextIndex = Math.max(0, Math.min(pages.length - 1, physicalPage1Based - 1));
      if (nextIndex === pageIndex) return;
      userNavigatedRef.current = true;
      setPageIndex(nextIndex);
    },
    [pageIndex, pages.length],
  );

  const handleThemeChange = (theme) => {
    setSelectedBackgroundTheme(theme);
    void updateUserBookDetail({ theme_id: theme.theme_meta_unique_id });
  };

  const decreaseFont = () => {
    setReaderFontScale((s) => Math.max(READER_FONT_SCALE_MIN, s - 0.1));
  };

  const increaseFont = () => {
    setReaderFontScale((s) => Math.min(READER_FONT_SCALE_MAX, s + 0.1));
  };

  if (initialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-violet-800 border-t-transparent" />
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-gray-700">This book could not be found.</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-lg bg-violet-900 px-4 py-2 text-white"
        >
          Go home
        </button>
      </div>
    );
  }

  if (error === 'load_failed') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-gray-700">Could not load this book. Try again later.</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" dir={readerTextDir}>
      <MobileReaderChrome
        onBack={() => navigate(-1)}
        pageIndex={pageIndex}
        totalPages={pages.length}
        onPrev={goPrev}
        onNext={goNext}
        onJumpToPage={jumpToPage}
        canPrev={canPrev}
        canNext={canNext}
        showFontControls
        fontScale={clampedFontScale}
        onFontDecrease={decreaseFont}
        onFontIncrease={increaseFont}
        backgroundThemes={backgroundThemes}
        selectedThemeId={selectedBackgroundTheme?.theme_meta_unique_id}
        onThemeChange={handleThemeChange}
        backgroundStyle={backgroundStyle}
      >
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
          <MobileAuthoredPage
            key={currentPage?.page_meta_unique_id ?? pageIndex}
            page={currentPage}
            extensions={extensions}
            fontScale={clampedFontScale}
            pageTheme={selectedPageTheme || FALLBACK_PAGE_THEME}
            textDir={readerTextDir}
          />
        </div>
      </MobileReaderChrome>
      {promoElement}
    </div>
  );
}
